'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { Navbar, Footer } from '../../components/Layout';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import Link from 'next/link';
import Image from 'next/image';
import { Filter, Search, ShoppingCart, Star, ChevronDown, SlidersHorizontal, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ProductImage from '../../components/ProductImage';
import ProductCard from '../../components/ProductCard';

const DUMMY_PRODUCTS = [
  { id: 1, name: 'Pure Palm Jaggery (Karupatti)', price: 340, category: 'Sweeteners', image: 'https://images.unsplash.com/photo-1622485558455-8cb5d54a2a16?q=80&w=800&auto=format&fit=crop' },
  { id: 2, name: 'Wood Pressed Groundnut Oil', price: 450, category: 'Oils & Ghee', image: 'https://images.unsplash.com/photo-1474625121024-7595bfbc57ac?q=80&w=800&auto=format&fit=crop' },
  { id: 3, name: 'Raw Forest Wild Honey', price: 650, category: 'Sweeteners', image: 'https://images.unsplash.com/photo-1587049352847-81a56d773c1c?q=80&w=800&auto=format&fit=crop' },
  { id: 4, name: 'A2 Kangayam Cow Ghee', price: 850, category: 'Oils & Ghee', image: 'https://images.unsplash.com/photo-1620916297397-a4a5402a3c6c?q=80&w=800&auto=format&fit=crop' },
  { id: 5, name: 'Karuppu Kavuni Laddu', price: 200, category: 'Snacks', image: 'https://images.unsplash.com/photo-1601050690597-df0568f70950?q=80&w=800&auto=format&fit=crop' },
  { id: 6, name: 'Organic Turmeric Powder', price: 150, category: 'Spices', image: 'https://images.unsplash.com/photo-1615485925600-97237c4fc1ec?q=80&w=800&auto=format&fit=crop' },
  { id: 7, name: 'Millet Murukku', price: 120, category: 'Snacks', image: 'https://images.unsplash.com/photo-1596647225114-11e5f8fbebd8?q=80&w=800&auto=format&fit=crop' },
  { id: 8, name: 'Hand-pounded Chilli Powder', price: 180, category: 'Spices', image: 'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?q=80&w=800&auto=format&fit=crop' },
];

const CATEGORIES_STATIC = ['All', 'Oils & Ghee', 'Sweeteners', 'Snacks', 'Spices'];

export default function ProductsPage() {
  const { addToCart } = useCart();
  const { t, language } = useLanguage();
  const [products, setProducts] = useState<any[]>(() => {
    if (typeof window !== 'undefined') {
      const cached = sessionStorage.getItem('pv_products_cache');
      if (cached) {
        try {
          return JSON.parse(cached);
        } catch (e) {}
      }
    }
    return [];
  });
  const [categoriesList, setCategoriesList] = useState<string[]>(['All', 'Oils & Ghee', 'Sweeteners', 'Snacks', 'Spices']);
  
  // Filter States
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [priceRange, setPriceRange] = useState<number>(5000); // max price filter initialized to 5000
  const [sortBy, setSortBy] = useState('Featured');

  // Mobile Drawer State
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  // Read URL query parameters on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const category = params.get('category');
      const search = params.get('search') || params.get('query');
      if (category) {
        setSelectedCategory(category);
      }
      if (search) {
        setSearchQuery(search);
      }
    }
  }, []);

  useEffect(() => {
    const apiUrl = process.env.NEXT_PUBLIC_PRODUCT_API || 'http://localhost:8081';
    
    // Fetch products
    fetch(`${apiUrl}/api/products?size=100`)
      .then(res => res.json())
      .then(data => {
         let contentList: any[] = [];
         if (data && data.data && data.data.content) {
           contentList = data.data.content;
         } else if (Array.isArray(data)) {
           contentList = data;
         }
         
         const mappedProducts = contentList.map((p: any) => {
             const firstVariant = p.productVariants && p.productVariants.length > 0 ? p.productVariants[0] : null;
             return {
                 id: p.id,
                 name: p.name,
                 price: firstVariant ? firstVariant.price : p.price,
                 oldPrice: firstVariant ? firstVariant.oldPrice : null,
                 category: p.categoryName || p.category?.name || 'Snacks',
                 image: p.images?.[0] || 'https://images.unsplash.com/photo-1596647225114-11e5f8fbebd8?q=80&w=800&auto=format&fit=crop',
                 images: p.images || [],
                 foodPreference: p.foodPreference || 'VEG',
                 slug: p.slug,
                 farmSource: p.farmSource,
                 showFarmSource: p.showFarmSource,
                 productVariants: p.productVariants
             };
         });
         setProducts(mappedProducts);
         if (typeof window !== 'undefined') {
           sessionStorage.setItem('pv_products_cache', JSON.stringify(mappedProducts));
         }
      })
      .catch(err => {
        console.error('Fetch error:', err);
        setProducts([]);
      });

    // Fetch categories dynamically
    fetch(`${apiUrl}/api/products/categories`)
      .then(res => res.json())
      .then(data => {
         let list: any[] = [];
         if (data && data.data) {
           list = data.data;
         } else if (Array.isArray(data)) {
           list = data;
         }
         if (list.length > 0) {
           const names = list.map((c: any) => c.name);
           setCategoriesList(['All', ...names]);
         }
      })
      .catch(err => console.warn('Fetch categories error:', err));
  }, []);

  const filteredProducts = useMemo(() => {
    return products
      .filter(p => selectedCategory === 'All' || p.category === selectedCategory)
      .filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()))
      .filter(p => p.price <= priceRange)
      .sort((a, b) => {
        if (sortBy === 'Price: Low to High') return a.price - b.price;
        if (sortBy === 'Price: High to Low') return b.price - a.price;
        return 0; // Featured/Default
      });
  }, [products, searchQuery, selectedCategory, priceRange, sortBy]);

  return (
    <div className="min-h-screen bg-surface font-body text-text-primary selection:bg-amber/30 selection:text-deep-green">
      <Navbar />
      
      {/* Header Banner */}
      <div className="hidden md:block bg-gradient-to-r from-deep-green to-mid-green text-white py-4 md:py-6 px-4 md:px-6 shadow-md border-b border-border">
        <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row items-center justify-between gap-3 md:gap-4">
          <div className="text-left">
            <h1 className="text-xl md:text-3xl font-display font-black mb-1">{t('products')}</h1>
            <p className="text-white/80 text-sm max-w-xl font-medium">Browse our complete collection of 100% certified organic products, sourced ethically.</p>
          </div>
          <div className="hidden md:flex gap-3">
            <span className="bg-white/20 backdrop-blur-md px-4 py-1.5 rounded-full text-xs font-bold border border-white/30 shadow-sm">🌱 100% Organic</span>
            <span className="bg-amber text-deep-green px-4 py-1.5 rounded-full text-xs font-bold border border-amber-600/30 shadow-sm">🚚 Free Shipping</span>
          </div>
        </div>
      </div>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 py-4 md:py-8 flex flex-col md:flex-row gap-4 md:gap-8 items-start relative">
        
        {/* Left Sidebar - Filters (hidden on mobile, sticky on desktop) */}
        <aside className="w-full md:w-80 bg-white p-6 md:p-8 rounded-3xl shadow-md border border-border/80 shrink-0 md:sticky md:top-24 hidden md:block text-left">
          <div className="flex items-center justify-between gap-2 font-bold text-deep-green mb-6 pb-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-mid-green" /> {t('filters')}
            </div>
            <span className="text-xs text-text-muted font-bold">
              {filteredProducts.length} Items
            </span>
          </div>

          <div className="mb-6">
            <h4 className="font-bold text-deep-green mb-3 text-xs uppercase tracking-wider">{t('search')}</h4>
            <div className="relative">
              <input 
                type="text" 
                placeholder={t('findProduct')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-surface border border-border/80 rounded-xl pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-mid-green transition-colors font-medium text-deep-green"
              />
              <Search className="w-4 h-4 text-text-muted absolute left-3 top-3.5" />
            </div>
          </div>

          <div className="mb-6">
            <h4 className="font-bold text-deep-green mb-3 text-xs uppercase tracking-wider">{t('category')}</h4>
            <div className="space-y-1.5">
              {categoriesList.map(cat => {
                const isActive = selectedCategory === cat;
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => {
                      setSelectedCategory(cat);
                      setSearchQuery('');
                    }}
                    className={`w-full text-left px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-between ${
                      isActive 
                        ? 'bg-mid-green text-white shadow-md shadow-mid-green/10' 
                        : 'text-deep-green hover:bg-pale-green/30'
                    }`}
                  >
                    <span>{cat}</span>
                    {isActive && <span className="w-1.5 h-1.5 rounded-full bg-amber"></span>}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mb-6">
            <h4 className="font-bold text-deep-green mb-3 text-xs uppercase tracking-wider">{t('maxPrice')}: <span className="text-amber font-black">₹{priceRange}</span></h4>
            <input 
              type="range" 
              min="50" 
              max="5000" 
              step="50"
              value={priceRange}
              onChange={(e) => setPriceRange(Number(e.target.value))}
              className="w-full h-1.5 bg-surface rounded-lg appearance-none cursor-pointer accent-mid-green"
            />
            <div className="flex justify-between text-[10px] font-bold text-text-muted mt-2">
              <span>₹50</span>
              <span>₹5000</span>
            </div>
          </div>

          {/* Sort By inside Filter Card */}
          <div className="mb-6">
            <h4 className="font-bold text-deep-green mb-3 text-xs uppercase tracking-wider">{t('sortBy')}</h4>
            <select 
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full bg-surface border border-border/80 rounded-xl px-4 py-2.5 text-xs font-bold text-deep-green focus:outline-none focus:border-mid-green shadow-sm transition-all"
            >
              <option>Featured</option>
              <option>Price: Low to High</option>
              <option>Price: High to Low</option>
            </select>
          </div>
          
          <button 
            type="button"
            onClick={() => {
              setSearchQuery('');
              setSelectedCategory('All');
              setPriceRange(5000);
              setSortBy('Featured');
            }}
            className="w-full py-3 bg-pale-green hover:bg-amber hover:text-white text-deep-green font-bold text-xs uppercase tracking-wider rounded-xl transition-all"
          >
            {t('clearFilters')}
          </button>
        </aside>

        {/* Right Side - Product Grid wrapped in unified grouped background container */}
        <div className="flex-1 w-full bg-transparent sm:bg-white rounded-none sm:rounded-3xl p-0 sm:p-6 md:p-8 border-0 sm:border border-border/80 shadow-none sm:shadow-sm text-left">
          
          {filteredProducts.length === 0 ? (
            <div className="py-20 text-center">
               <div className="w-20 h-20 bg-pale-green/40 rounded-full flex items-center justify-center mx-auto mb-5 shadow-inner">
                 <Search className="w-10 h-10 text-mid-green" />
               </div>
               <h3 className="text-2xl font-bold text-deep-green mb-2">{t('noProductsFound')}</h3>
               <p className="text-text-muted text-sm font-medium">{t('noProductsDesc')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-6 md:gap-8">
              {filteredProducts.map((p, i) => (
                <ProductCard 
                  key={i} 
                  product={p} 
                  onAddToCart={addToCart} 
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Mobile Floating Filters button */}
      <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-40 md:hidden animate-bounce">
        <button
          onClick={() => setIsMobileDrawerOpen(true)}
          className="bg-deep-green text-white px-5 py-3 rounded-full font-black text-xs uppercase tracking-wider shadow-lg flex items-center gap-1.5 border border-white/10"
        >
          <SlidersHorizontal className="w-3.5 h-3.5 text-amber" /> Filters & Sort
        </button>
      </div>

      {/* Mobile Full-Screen Filter Drawer */}
      <AnimatePresence>
        {isMobileDrawerOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex justify-end md:hidden text-left"
          >
            {/* Backdrop click close */}
            <div className="absolute inset-0" onClick={() => setIsMobileDrawerOpen(false)} />
            
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.25 }}
              className="w-80 bg-white h-full p-6 overflow-y-auto flex flex-col justify-between relative z-10 shadow-2xl"
            >
              <div>
                <div className="flex justify-between items-center pb-4 mb-6 border-b border-border">
                  <h3 className="font-display font-black text-lg text-deep-green uppercase tracking-wide">Filters & Sort</h3>
                  <button 
                    onClick={() => setIsMobileDrawerOpen(false)} 
                    className="p-1.5 rounded-full hover:bg-surface border border-border"
                  >
                    <X className="w-4.5 h-4.5 text-deep-green" />
                  </button>
                </div>
                
                {/* Search */}
                <div className="mb-6">
                  <h4 className="font-bold text-deep-green mb-3 text-xs uppercase tracking-wider">{t('search')}</h4>
                  <div className="relative">
                    <input 
                      type="text" 
                      placeholder={t('findProduct')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full bg-surface border border-border/80 rounded-xl pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-mid-green transition-colors font-medium text-deep-green"
                    />
                    <Search className="w-4 h-4 text-text-muted absolute left-3 top-3.5" />
                  </div>
                </div>

                {/* Category */}
                <div className="mb-6">
                  <h4 className="font-bold text-deep-green mb-3 text-xs uppercase tracking-wider">{t('category')}</h4>
                  <div className="space-y-1.5">
                    {categoriesList.map(cat => {
                      const isActive = selectedCategory === cat;
                      return (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => {
                            setSelectedCategory(cat);
                            setSearchQuery('');
                          }}
                          className={`w-full text-left px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center justify-between ${
                            isActive ? 'bg-mid-green text-white' : 'text-deep-green hover:bg-pale-green/30'
                          }`}
                        >
                          <span>{cat}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Max Price */}
                <div className="mb-6">
                  <h4 className="font-bold text-deep-green mb-3 text-xs uppercase tracking-wider">{t('maxPrice')}: <span className="text-amber font-black">₹{priceRange}</span></h4>
                  <input 
                    type="range" 
                    min="50" 
                    max="5000" 
                    step="50"
                    value={priceRange}
                    onChange={(e) => setPriceRange(Number(e.target.value))}
                    className="w-full h-1 bg-surface rounded-lg appearance-none cursor-pointer accent-mid-green"
                  />
                </div>

                {/* Sort By */}
                <div className="mb-6">
                  <h4 className="font-bold text-deep-green mb-3 text-xs uppercase tracking-wider">{t('sortBy')}</h4>
                  <select 
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full bg-surface border border-border/80 rounded-xl px-4 py-2.5 text-xs font-bold text-deep-green focus:outline-none"
                  >
                    <option>Featured</option>
                    <option>Price: Low to High</option>
                    <option>Price: High to Low</option>
                  </select>
                </div>
              </div>

              <div className="space-y-3 pt-6 border-t border-border mt-auto">
                <button 
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategory('All');
                    setPriceRange(5000);
                    setSortBy('Featured');
                    setIsMobileDrawerOpen(false);
                  }}
                  className="w-full py-3 bg-pale-green hover:bg-amber hover:text-white text-deep-green font-bold text-xs uppercase tracking-wider rounded-xl transition-all"
                >
                  {t('clearFilters')}
                </button>
                <button 
                  type="button"
                  onClick={() => setIsMobileDrawerOpen(false)}
                  className="w-full py-3 bg-deep-green text-white font-bold text-xs uppercase tracking-wider rounded-xl transition-all"
                >
                  Apply Filters
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
